import {
  KLEUREN_OPVRAGEN_META_BODY,
  KLEUREN_OPVRAGEN_NAAM,
} from '@/features/whatsapp/lib/kleurenOpvragenTemplate';

export interface WhatsAppTemplateVariable {
  key: string;
  label: string;
  placeholder: string;
}

/**
 * Dynamische URL-knop onder de template (Meta "Call to action" button met
 * variabel URL-deel). De volledige link wordt urlPrefix + waarde van
 * variableKey; Meta vereist dat de parameter bij verzending wordt meegestuurd.
 */
export interface WhatsAppTemplateUrlButton {
  label: string;
  urlPrefix: string;
  /** Sleutel in de values-map met het variabele URL-deel (bv. afspraak-token). */
  variableKey: string;
}

export interface WhatsAppTemplate {
  name: string;
  displayName: string;
  description: string;
  category: 'afspraak' | 'offerte' | 'project' | 'factuur' | 'algemeen';
  hasHeader: boolean;
  variables: WhatsAppTemplateVariable[];
  previewBody: string;
  urlButton?: WhatsAppTemplateUrlButton;
  /**
   * Template die vanuit de app bij Meta wordt aangemeld (Instellingen → Verf)
   * en dus pas verstuurbaar is zodra Meta hem heeft goedgekeurd. De
   * templatekiezer haalt dan de Meta-status op en zet hem grijs zolang die
   * niet APPROVED is (melding 1145695b).
   */
  metaStatusVereist?: boolean;
}

export const WHATSAPP_TEMPLATES: WhatsAppTemplate[] = [
  {
    name: 'afspraak_bevestiging',
    displayName: 'Afspraak bevestiging',
    description: 'Bevestig een opname-afspraak met de klant',
    category: 'afspraak',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adviseur', label: 'Adviseur', placeholder: 'Mark Jansen' },
      { key: 'datum', label: 'Datum', placeholder: 'dinsdag 11 maart' },
      { key: 'tijd_van', label: 'Tijd van', placeholder: '13:00' },
      { key: 'tijd_tot', label: 'Tijd tot', placeholder: '15:00' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nGraag bevestigen wij de opname naar aanleiding van uw offerteaanvraag. De afspraak staat gepland met {{2}}, die zal op {{3}} tussen {{4}} en {{5}} aanwezig zijn op: {{6}}.\n\nHeeft u vragen? Stuur ons gerust een bericht!\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
  {
    name: 'afspraak_herinnering',
    displayName: 'Afspraak herinnering',
    description: 'Herinner de klant aan een afspraak van morgen',
    category: 'afspraak',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adviseur', label: 'Adviseur', placeholder: 'Mark Jansen' },
      { key: 'tijd_van', label: 'Tijd van', placeholder: '13:00' },
      { key: 'tijd_tot', label: 'Tijd tot', placeholder: '15:00' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nDit is een herinnering voor uw afspraak van morgen betreffende de opname van de geplande werkzaamheden.\n\nDe afspraak staat gepland met onze adviseur {{2}}, die tussen {{3}} en {{4}} aanwezig zal zijn op het volgende adres: {{5}}.\n\nGraag tot morgen! Mocht u verhinderd zijn, dan kunt u de afspraak bekijken of verplaatsen via de knop hieronder.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
    urlButton: {
      label: 'Afspraak bekijken/verplaatsen',
      urlPrefix: 'https://prachttotaalonderhoud.app/afspraak/',
      variableKey: 'afspraak_token',
    },
  },
  {
    name: 'offerte_verstuurd',
    displayName: 'Offerte verstuurd',
    description: 'Laat de klant weten dat de offerte is verstuurd',
    category: 'offerte',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nWij hebben zojuist een offerte naar u verstuurd voor de werkzaamheden op {{2}}. U kunt de offerte bekijken en direct digitaal ondertekenen via de knop hieronder.\n\nHeeft u vragen? Stuur ons gerust een bericht!\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
    urlButton: {
      label: 'Offerte bekijken & ondertekenen',
      urlPrefix: 'https://prachttotaalonderhoud.app/offerte/tekenen/',
      variableKey: 'offerte_token',
    },
  },
  {
    name: 'offerte_opvolging_1',
    displayName: 'Offerte opvolging 1 (dag 7)',
    description: 'Eerste opvolging na verzenden offerte (dag 7)',
    category: 'offerte',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nEnige tijd geleden ontving u van ons een offerte voor de werkzaamheden op {{2}}. Wij zijn benieuwd of u de offerte heeft kunnen bekijken en of alles duidelijk is.\n\nHeeft u vragen of wilt u iets aanpassen? Laat het ons gerust weten. U kunt de offerte ook direct bekijken en ondertekenen via de knop hieronder.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
    urlButton: {
      label: 'Offerte bekijken & ondertekenen',
      urlPrefix: 'https://prachttotaalonderhoud.app/offerte/tekenen/',
      variableKey: 'offerte_token',
    },
  },
  {
    name: 'offerte_opvolging_2',
    displayName: 'Offerte opvolging 2 (dag 14)',
    description: 'Tweede opvolging na verzenden offerte (dag 14)',
    category: 'offerte',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nWij willen u graag nogmaals herinneren aan onze offerte voor de werkzaamheden op {{2}}. Mocht u nog twijfels of vragen hebben, dan denken wij graag met u mee.\n\nU kunt de offerte bekijken en ondertekenen via de knop hieronder.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
    urlButton: {
      label: 'Offerte bekijken & ondertekenen',
      urlPrefix: 'https://prachttotaalonderhoud.app/offerte/tekenen/',
      variableKey: 'offerte_token',
    },
  },
  {
    name: 'offerte_opvolging_3',
    displayName: 'Offerte opvolging 3 (dag 21)',
    description: 'Laatste opvolging na verzenden offerte (dag 21)',
    category: 'offerte',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nDit is onze laatste herinnering betreffende de offerte voor de werkzaamheden op {{2}}. Wij horen graag of u met ons in zee wilt gaan, zodat wij een plek in de planning voor u kunnen reserveren.\n\nHeeft u besloten om (voorlopig) geen gebruik te maken van onze offerte? Ook dat horen wij graag, dan sluiten wij het dossier netjes voor u af.\n\nU kunt de offerte bekijken en ondertekenen via de knop hieronder.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
    urlButton: {
      label: 'Offerte bekijken & ondertekenen',
      urlPrefix: 'https://prachttotaalonderhoud.app/offerte/tekenen/',
      variableKey: 'offerte_token',
    },
  },
  {
    name: 'opdrachtbevestiging',
    displayName: 'Opdrachtbevestiging',
    description: 'Bevestig de opdracht na ondertekening offerte',
    category: 'project',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nWij hebben uw akkoord op de offerte in goede orde ontvangen — hartelijk dank voor het vertrouwen!\n\nUw project op {{2}} is in behandeling genomen. Onze werkvoorbereider gaat voor u aan de slag en neemt binnenkort contact met u op over de planning en verdere voorbereiding.\n\nHeeft u in de tussentijd vragen? U kunt ons altijd bereiken via dit nummer.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
  {
    name: 'planning_startdatum',
    displayName: 'Planning startdatum',
    description: 'Informeer de klant over de startdatum',
    category: 'project',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
      { key: 'startdatum', label: 'Startdatum', placeholder: 'maandag 24 maart' },
    ],
    previewBody:
      'Beste {{1}},\n\nGoed nieuws! De werkzaamheden op {{2}} starten op {{3}}. Onze voorman zal zich bij u melden.\n\nHeeft u vragen? Stuur ons een bericht.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
  {
    name: 'planning_vertraging',
    displayName: 'Planning vertraging',
    description: 'Informeer de klant over vertraging',
    category: 'project',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nHelaas moeten wij u informeren dat de werkzaamheden op {{2}} vertraging hebben opgelopen. Wij houden u op de hoogte van de nieuwe planning.\n\nOnze excuses voor het ongemak.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
  {
    name: 'factuur_verstuurd',
    displayName: 'Factuur verstuurd',
    description: 'Laat de klant weten dat een factuur is verstuurd',
    category: 'factuur',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'factuurnummer', label: 'Factuurnummer', placeholder: 'F2026-0042' },
      { key: 'bedrag', label: 'Bedrag', placeholder: '€ 12.500,00' },
    ],
    previewBody:
      'Beste {{1}},\n\nWij hebben factuur {{2}} ter waarde van {{3}} naar u verstuurd per e-mail.\n\nHeeft u vragen over de factuur? Stuur ons gerust een bericht.\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
  {
    name: 'oplevering_ingepland',
    displayName: 'Oplevering ingepland',
    description: 'Informeer de klant over de opleverdatum',
    category: 'project',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
      { key: 'datum', label: 'Datum', placeholder: 'vrijdag 28 maart' },
      { key: 'tijd', label: 'Tijd', placeholder: '10:00' },
    ],
    previewBody:
      'Beste {{1}},\n\nDe oplevering van uw project op {{2}} is ingepland op {{3}} om {{4}} uur. Wij verzoeken u hierbij aanwezig te zijn.\n\nTot dan!\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
  {
    name: 'project_afgerond',
    displayName: 'Project afgerond',
    description: 'Laat de klant weten dat het project is afgerond',
    category: 'project',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody:
      'Beste {{1}},\n\nDe werkzaamheden op {{2}} zijn afgerond. Wij hopen dat u tevreden bent met het resultaat. Uw garantiecertificaat is per e-mail verstuurd.\n\nWij horen graag uw ervaring!\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
  {
    // Tekst akkoord Tom 01-10 (melding Pieter); alleen tekst, geen afbeelding-header,
    // zodat hij via Instellingen → Verf bij Meta is aan te melden (zonder header).
    name: KLEUREN_OPVRAGEN_NAAM,
    metaStatusVereist: true,
    displayName: 'Kleuren opvragen',
    description: 'Vraag de klant om kleurcodes of een oud verfblik; anders komen wij meten',
    category: 'project',
    hasHeader: false,
    variables: [
      { key: 'naam', label: 'Naam', placeholder: 'mevrouw De Vries' },
      { key: 'adres', label: 'Adres', placeholder: 'Kerkstraat 12, 1234 AB Amsterdam' },
    ],
    previewBody: KLEUREN_OPVRAGEN_META_BODY,
  },
  {
    name: 'algemeen_bericht',
    displayName: 'Algemeen bericht',
    description: 'Stuur een vrij bericht naar de klant (met eigen tekst)',
    category: 'algemeen',
    hasHeader: true,
    variables: [
      { key: 'klantnaam', label: 'Klantnaam', placeholder: 'Jan de Vries' },
      { key: 'bericht', label: 'Bericht', placeholder: 'Uw eigen tekst hier...' },
    ],
    previewBody:
      'Beste {{1}},\n\n{{2}}\n\nMet vriendelijke groet,\nHet team van Pracht Totaalonderhoud B.V.',
  },
];

export const HEADER_IMAGE_URL = 'https://prachttotaalonderhoud.app/Asset_33.png';

export function buildTemplateComponents(
  template: WhatsAppTemplate,
  values: Record<string, string>,
): unknown[] {
  const components: unknown[] = [];

  if (template.hasHeader) {
    components.push({
      type: 'header',
      parameters: [{ type: 'image', image: { link: HEADER_IMAGE_URL } }],
    });
  }

  const bodyParams = template.variables.map((v) => ({
    type: 'text',
    text: values[v.key] || '',
  }));

  if (bodyParams.length > 0) {
    components.push({
      type: 'body',
      parameters: bodyParams,
    });
  }

  // Dynamische URL-knop: Meta verwacht het variabele URL-deel als parameter
  // op een button-component (index 0 = eerste knop in de template).
  if (template.urlButton && values[template.urlButton.variableKey]) {
    components.push({
      type: 'button',
      sub_type: 'url',
      index: '0',
      parameters: [{ type: 'text', text: values[template.urlButton.variableKey] }],
    });
  }

  return components;
}

export function getTemplatePreview(
  template: WhatsAppTemplate,
  values: Record<string, string>,
): string {
  let preview = template.previewBody;
  template.variables.forEach((v, i) => {
    const val = values[v.key] || v.placeholder;
    preview = preview.replace(`{{${i + 1}}}`, val);
  });
  return preview;
}

export function getTemplateByName(name: string): WhatsAppTemplate | undefined {
  return WHATSAPP_TEMPLATES.find((t) => t.name === name);
}

/** Leesbare naam voor een template, bv. voor weergave in de inbox. */
export function getTemplateDisplayName(name: string): string {
  return getTemplateByName(name)?.displayName ?? name;
}

/**
 * Volledige berichttekst van een template met de invulvelden als leesbare
 * labels (bv. "[klantnaam]"). Voor oude templateberichten waarvan de
 * ingevulde waarden nooit zijn opgeslagen: zo is de verstuurde tekst alsnog
 * te lezen, alleen de klantspecifieke gegevens staan als label.
 */
export function getTemplateBodyMetLabels(name: string): string | null {
  const template = getTemplateByName(name);
  if (!template) return null;
  let body = template.previewBody;
  template.variables.forEach((v, i) => {
    body = body.replace(`{{${i + 1}}}`, `[${v.label.toLowerCase()}]`);
  });
  return body;
}

/**
 * Reconstrueert de uiteindelijke berichttekst uit de body-parameters van de
 * Meta-componenten. Zo kan de ingevulde tekst als `body` worden opgeslagen bij
 * verzending, zodat het bericht leesbaar is in de eigen WhatsApp-inbox.
 */
export function renderTemplateBody(templateName: string, components?: unknown[]): string | null {
  const template = getTemplateByName(templateName);
  if (!template) return null;

  const bodyComponent = (components || []).find((c) => (c as { type?: string }).type === 'body') as
    | { parameters?: { type?: string; text?: string }[] }
    | undefined;
  const params = bodyComponent?.parameters || [];

  let body = template.previewBody;
  template.variables.forEach((_v, i) => {
    body = body.replace(`{{${i + 1}}}`, params[i]?.text ?? '');
  });
  return body;
}
